import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  TextField,
  Autocomplete,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  FormControlLabel,
  FormControl,
  FormGroup,
  Typography,
  Checkbox
} from "@mui/material";
import {
  MailFormatData,
  MailTemplateData,
  ToUser,
  DefaultConfig,
  MailRecipientTargets
} from "../../../data/WorkFlowEditorType";
import MailBodyComponent from "../../../../MailTemplate/component/MailBodyComponent";

/**
 * メールフォーマット編集ダイアログコンポーネント
 * - アクティビティに紐づくメール件名・本文・宛先を設定する
 * - テンプレートを選択して内容を上書きするか、直接編集できる
 * - props: open (ダイアログ表示状態), onClose (閉じるコールバック),
 *          mailTemplateList (選択可能なテンプレート一覧), activityId (対象アクティビティID),
 *          mailFormat (現在のフォーマット設定), handleChangeMailFormat (変更保存コールバック)
 */
const EditMailFormatDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  handleEditMailTemplate: () => void;
  handleChangeMailRecipientTargets: (id: string, targets: MailRecipientTargets) => void;
  mailTemplateList: MailTemplateData[];
  selectToUserList: ToUser[];
  activityId: string;
  mailFormat?: MailFormatData;
  defaultConfig?: DefaultConfig;
  handleChangeMailFormat: (
    id: string,
    subject: string,
    body: string,
    toUsers: ToUser[]
  ) => void;
}> = ({
  open,
  onClose,
  handleEditMailTemplate,
  mailTemplateList,
  selectToUserList,
  activityId,
  mailFormat,
  defaultConfig,
  handleChangeMailFormat,
  handleChangeMailRecipientTargets
}) => {
    const [toUsers, setToUsers] = useState<ToUser[]>(
      mailFormat?.toUsers ?? []
    );
    const [subject, setSubject] = useState<string>(
      mailFormat?.subject ?? ""
    );
    const [selectedTemplate, setSelectedTemplate] = useState<MailTemplateData | null>(null);
    const [recipientTargets, setRecipientTargets] = useState<MailRecipientTargets>(
      defaultConfig?.mail?.recipientTargets ?? { applicant: true, approver: false, referent: false }
    );

    const bodyRef = useRef<any>(null);

    /** 初期処理 */
    useEffect(() => {
      if (!open) return;

      setToUsers(mailFormat?.toUsers ?? []);
      setSubject(mailFormat?.subject ?? "");
      setRecipientTargets(
        defaultConfig?.mail?.recipientTargets ?? { applicant: true, approver: false, referent: false }
      );

      if (bodyRef.current) {
        bodyRef.current.setValue(mailFormat?.body ?? "");
      }
    }, [mailFormat, defaultConfig, open]);

    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>メールフォーマット</DialogTitle>
        <DialogContent>
          <Box
            sx={{ display: "flex", flexDirection: "column", rowGap: 2, mt: 1 }}
          >
            <Grid container spacing={2}>
              <Grid container size={12}>
                <Grid size={6}>
                  <Autocomplete
                    options={mailTemplateList}
                    getOptionLabel={(option) => option.name}
                    value={selectedTemplate}

                    onChange={(_, tpl) => {
                      setSelectedTemplate(tpl);

                      const nextToUsers = tpl?.toUsers ?? [];
                      const nextSubject = tpl?.subject ?? "";
                      const nextBody = tpl?.body ?? "";

                      // 選択したテンプレートデータを即時UIに反映
                      setToUsers(nextToUsers);
                      setSubject(nextSubject);
                      bodyRef.current?.setValue(nextBody);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="メールテンプレート（サンプル）" fullWidth />
                    )}
                  />

                </Grid>
                <Grid size={3}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <Chip
                      label="リセット"
                      color="primary"
                      onClick={() => {
                        setSelectedTemplate(null);
                        setToUsers(mailFormat?.toUsers ?? []);
                        setSubject(mailFormat?.subject ?? "");
                        bodyRef.current?.setValue(mailFormat?.body ?? "");

                        setRecipientTargets(
                          defaultConfig?.mail?.recipientTargets ?? { applicant: true, approver: false, referent: false }
                        );

                      }}
                    />
                  </Box>
                </Grid>
                <Grid size={3}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <Button onClick={handleEditMailTemplate}>
                      テンプレートを追加・編集
                    </Button>
                  </Box>
                </Grid>
              </Grid>
              <Grid size={12}>
                <Autocomplete
                  options={selectToUserList}
                  getOptionLabel={(option) =>
                    `${option.lastName} ${option.firstName}`
                  }
                  value={selectToUserList.filter((r) =>
                    toUsers?.some((u) => u.id === r.id)
                  )}
                  onChange={(_, value) => setToUsers(value)}
                  multiple
                  renderInput={(params) => (
                    <TextField {...params} label="宛先" fullWidth />
                  )}
                />
                <FormControl component="fieldset">
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    送信対象
                  </Typography>
                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!recipientTargets.applicant}
                          onChange={(e) =>
                            setRecipientTargets((prev) => ({ ...prev, applicant: e.target.checked }))
                          }
                        />
                      }
                      label="申請者"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!recipientTargets.approver}
                          onChange={(e) =>
                            setRecipientTargets((prev) => ({ ...prev, approver: e.target.checked }))
                          }
                        />
                      }
                      label="承認者"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!recipientTargets.referent}
                          onChange={(e) =>
                            setRecipientTargets((prev) => ({ ...prev, referent: e.target.checked }))
                          }
                        />
                      }
                      label="参照者"
                    />
                  </FormGroup>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <TextField
                  label="件名"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>
            <MailBodyComponent
              bodyRef={bodyRef}
              defaultValue={mailFormat?.body ?? ""}
              height={400}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Button variant="outlined" onClick={onClose}>
              キャンセル
            </Button>
            <Button
              variant="contained"
              disabled={
                !subject && (!bodyRef.current || !bodyRef.current.getValue())
              }
              onClick={() => {
                handleChangeMailFormat(
                  activityId,
                  subject,
                  bodyRef.current?.getValue() || "",
                  toUsers
                );

                // 受信側情報取得
                handleChangeMailRecipientTargets(activityId, {
                  applicant: !!recipientTargets.applicant,
                  approver: !!recipientTargets.approver,
                  referent: !!recipientTargets.referent,
                });

                onClose();
              }}
            >
              保存
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    );
  };

export default EditMailFormatDialog;
